/**
 * Procrastination Blackjack (SPEC.md §9). Entirely client-side and entirely ephemeral.
 *
 * §9: "Rules are fixed. Implement exactly as written. Do not add mechanics."
 *
 *     21           you do none of them
 *     18-20        do one, right now
 *     17 or less   do two
 *     bust (>21)   do all three
 *
 * §9.1 dealing: standard blackjack, single 52-card deck shuffled per hand, number cards
 * face value, face cards 10, aces 11 or 1 — take 11 unless that busts, then 1. Deal two,
 * then hit or stand freely, to a maximum of six cards. No five-card rule, no dealer, no
 * splits, no doubling.
 *
 * Nothing here is persisted or sent anywhere. A hand is a way of choosing what to do next,
 * not a thing worth recording — the follow-through shows up when the tasks get ticked off,
 * which the rest of the app already tracks. The only write the page makes is creating a
 * task from a freshly typed title (§9.2 option 1), through the ordinary task endpoint.
 */

export const RANKS = [
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
  "A",
] as const;
export const SUITS = ["S", "H", "D", "C"] as const;

export type Rank = (typeof RANKS)[number];
export type Suit = (typeof SUITS)[number];
export type Card = { rank: Rank; suit: Suit };

export const BLACKJACK = 21;
const ACE_HIGH = 11;
const ACE_LOW = 1;
export const OPENING_CARDS = 2;
export const MAX_CARDS = 6; // §9.1: "to a maximum of six cards"
export const SLOT_COUNT = 3; // §9: "three tasks you've been avoiding"

export type Outcome = "none" | "one" | "two" | "all";

export const TASKS_OWED: Record<Outcome, number> = { none: 0, one: 1, two: 2, all: 3 };

export const OUTCOME_TEXT: Record<Outcome, string> = {
  none: "21 - you do none of them",
  one: "Do one, right now",
  two: "Do two",
  all: "Bust - do all three",
};

/** A single 52-card deck, shuffled (§9.1). */
export function freshDeck(random: () => number = Math.random): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) for (const rank of RANKS) deck.push({ rank, suit });

  // Fisher-Yates. Every ordering equally likely; the naive sort-by-random is not.
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

/** Face value; face cards 10; an ace counts high here and is adjusted in `handTotal`. */
export function cardValue(card: Card): number {
  if (card.rank === "A") return ACE_HIGH;
  if (card.rank === "J" || card.rank === "Q" || card.rank === "K") return 10;
  return Number(card.rank);
}

/**
 * §9.1: "Aces count 11 or 1 — take 11 unless that busts the hand, then 1".
 *
 * Every ace starts at 11 and is demoted one at a time, only while the hand is bust. That is
 * the standard soft-total rule, and it yields the highest total that is not a bust.
 */
export function handTotal(cards: Card[]): number {
  let total = cards.reduce((sum, card) => sum + cardValue(card), 0);
  let acesHigh = cards.filter((card) => card.rank === "A").length;

  while (total > BLACKJACK && acesHigh > 0) {
    total -= ACE_HIGH - ACE_LOW;
    acesHigh -= 1;
  }
  return total;
}

/**
 * Map a final total to §9's outcome table.
 *
 * §9 originally read "Under 17", leaving a total of exactly 17 uncovered. Confirmed as
 * "17 or less" and amended in SPEC.md — folding downward keeps the mapping monotonic, so
 * standing higher never costs you more work.
 */
export function outcomeFor(total: number): Outcome {
  if (total > BLACKJACK) return "all"; // bust
  if (total === BLACKJACK) return "none";
  if (total >= 18 && total <= 20) return "one";
  return "two"; // 17 or less
}

export type Hand = {
  deck: Card[];
  cards: Card[];
  finished: boolean;
};

/**
 * Deal the opening two (§9.1).
 *
 * A dealt 21 ends there. Hitting on 21 can only bust, so the choice §9.1 offers has exactly
 * one sane answer, and making you press Stand to collect the best hand in the game is a beat
 * of ceremony in the wrong place.
 */
export function deal(random: () => number = Math.random): Hand {
  const deck = freshDeck(random);
  const cards = deck.splice(0, OPENING_CARDS);
  return { deck, cards, finished: handTotal(cards) === BLACKJACK };
}

export function isBust(hand: Hand): boolean {
  return handTotal(hand.cards) > BLACKJACK;
}

export function canHit(hand: Hand): boolean {
  return !hand.finished && !isBust(hand) && hand.cards.length < MAX_CARDS;
}

/** Take one more card. Busting, reaching 21, or reaching six cards ends the hand (§9.1). */
export function hit(hand: Hand): Hand {
  if (!canHit(hand)) return hand;

  const deck = [...hand.deck];
  const cards = [...hand.cards, deck.shift()!];
  const next: Hand = { deck, cards, finished: false };
  next.finished = isBust(next) || cards.length >= MAX_CARDS || handTotal(cards) === BLACKJACK;
  return next;
}

/** §9.1: "Standing low is allowed and simply means two tasks". */
export function stand(hand: Hand): Hand {
  return { ...hand, finished: true };
}

/** What each outcome costs you, phrased for a table cell rather than a sentence. */
export const OUTCOME_OWED: Record<Outcome, string> = {
  none: "none of them",
  one: "one, right now",
  two: "two",
  all: "all three",
};

/** The §9 outcome table, derived from `outcomeFor` so the two cannot disagree. */
export function outcomeBands(): {
  range: string;
  outcome: Outcome;
  text: string;
  owed: string;
}[] {
  const MIN_TOTAL = 4; // the lowest a two-card hand can reach
  const bands: { low: number; high: number; outcome: Outcome }[] = [];

  for (let total = MIN_TOTAL; total <= BLACKJACK; total++) {
    const outcome = outcomeFor(total);
    const last = bands[bands.length - 1];
    if (last && last.outcome === outcome) last.high = total;
    else bands.push({ low: total, high: total, outcome });
  }

  const table = bands.map((band) => ({
    range:
      band.low === MIN_TOTAL
        ? `${band.high} or less`
        : band.low === band.high
          ? `${band.low}`
          : `${band.low}–${band.high}`,
    outcome: band.outcome,
    text: OUTCOME_TEXT[band.outcome],
    owed: OUTCOME_OWED[band.outcome],
  }));

  // Kept numeric rather than the word "bust": the coverage test parses these ranges to
  // prove every reachable total maps somewhere, which is what stops the 17 gap coming back.
  table.push({
    range: `over ${BLACKJACK}`,
    outcome: "all",
    text: OUTCOME_TEXT.all,
    owed: OUTCOME_OWED.all,
  });
  return table;
}
